import { useDataStore } from './DataStore';
import { DataPartState } from './types';
import { useCallback, useMemo } from 'react';
import lodashCurry from 'lodash/curry';

export type SetDataPartFunction<TDataPart> = (
  currentState: DataPartState<TDataPart>,
) => DataPartState<TDataPart>;

export type UseDataPartReturnProps<TDataPart> = [
  dataPartState: DataPartState<TDataPart>,
  setDataPartState: (fn: SetDataPartFunction<TDataPart>) => void,
];

export const useDataPart = <TDataPart>(
  key: string[],
): UseDataPartReturnProps<TDataPart> => {
  const { getDataPartState, updateDataPartState: dataStoreUpdateDataPart } =
    useDataStore();

  const curriedDataStoreUpdateDataPart = useMemo(() => {
    return lodashCurry<string[], DataPartState<TDataPart>, void>(
      dataStoreUpdateDataPart,
    )(key);
  }, [dataStoreUpdateDataPart, key]);

  const {
    isDirty = null,
    isValid = null,
    isValidAsDraft = null,
    data: dataPartData = null,
  } = getDataPartState(key) || {};

  const dataPartDataState: DataPartState<TDataPart> = useMemo(() => {
    return {
      isDirty,
      isValid,
      isValidAsDraft,
      data: dataPartData,
    };
  }, [dataPartData, isDirty, isValid, isValidAsDraft]);

  const updateDataPart = useCallback(
    (fn: SetDataPartFunction<TDataPart>): void => {
      const newState = fn(dataPartDataState);
      curriedDataStoreUpdateDataPart(newState);
    },
    [curriedDataStoreUpdateDataPart, dataPartDataState],
  );

  return [dataPartDataState, updateDataPart];
};
